import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

export const useWebSocket = () => {
  const [connected, setConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<any>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    let mounted = true

    // Only connect if backend is available
    const checkBackendAndConnect = async () => {
      try {
        const healthUrl = process.env.NODE_ENV === 'production' 
          ? '/health' 
          : 'http://localhost:8002/health'
        const response = await fetch(healthUrl, { 
          signal: AbortSignal.timeout(5000) 
        })
        if (response.ok && mounted) {
          connectWebSocket()
        }
      } catch {
        console.log('Backend not available, skipping WebSocket')
      }
    }

    const connectWebSocket = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return

      const wsUrl = process.env.NODE_ENV === 'production' 
        ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws` 
        : 'ws://localhost:8002/ws'
      
      console.log('Attempting WebSocket connection to:', wsUrl)
      const ws = new WebSocket(wsUrl)
      
      ws.onopen = () => {
        if (mounted) {
          console.log('WebSocket connected successfully!')
          setConnected(true)
        }
      }

      ws.onmessage = (event) => {
        if (!mounted) return
        
        try {
          const data = JSON.parse(event.data)
          setLastMessage(data)
          
          // Update React Query cache based on message type
          if (data.type === 'article_updated') {
            queryClient.invalidateQueries({ queryKey: ['articles'] })
          }
          if (data.type === 'stats_updated') {
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      ws.onclose = () => {
        if (mounted) {
          console.log('WebSocket disconnected')
          setConnected(false)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      wsRef.current = ws
    }

    checkBackendAndConnect()

    return () => {
      mounted = false
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [queryClient])

  const send = (data: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data))
    }
  }

  return {
    connected,
    lastMessage,
    send
  }
} 